import axios from "axios";

import { APIErrorResponse } from "shared/api/api";
import {
  API_DATE_FORMAT,
  API_DATE_FORMAT_W_TIME,
  ISO_FORMAT,
} from "shared/constants";
import { formatDate } from "shared/locale/format";
import { DataType, SortBy } from "shared/types";
import { splitIgnoringDoubleQuotes, wrapInQuotesAndJoin } from "shared/utils";

import { FILTER_OPERATOR_TO_API_FILTER } from "features/ui/Filters/constants";
import { FilterRowState } from "features/ui/Filters/FilterBuilder/types";
import {
  FilterOperator,
  FilterState,
  OccursFilterOptionKeys,
} from "features/ui/Filters/types";
import {
  getAttributesFromFilter,
  getOperatorFromAPIOperator,
  isFilterStateCorrect,
} from "features/ui/Filters/utils";
import { SchemaEntry } from "features/ui/MaterialTable";

export type APIFilterOp =
  | "eq"
  | "neq"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "between"
  | "in"
  | "nin"
  | "is"
  | "isn"
  | "like"
  | "nlike"
  | "match"
  | "inlast"
  | "startswith"
  | "nstartswith"
  | "occurs"
  | "noccurs"
  | "exists"
  | "nexists";

type APIFilterValue =
  string | number | null | string[] | number[] | undefined | boolean;

export interface APIFilter {
  name: string;
  value: APIFilterValue;
  op: APIFilterOp;
}

export const getOptionsQuery = (
  options: Partial<Record<OccursFilterOptionKeys, string | number>>
): string => {
  let query = `windowSize="${options.windowSize}",windowType="${options.windowType}",windowDirection="${options.windowDirection}"`;

  if (options.dateField) {
    query += `,dateField="${options.dateField}"`;
  }

  return query;
};

const getValuesArray = (values: string): string[] => {
  // catches pipes (|) within the string that are not within quotes ("") and splits values on that
  // - this allows us to have | inside our values
  const regex = /\|+(?=(?:(?:[^"]*"){2})*[^"]*$)/g;

  return values.split(regex).map((str) => str.replaceAll('"', ""));
};

const filterValuesHaveEmbeddedFilter = (operator: FilterOperator) =>
  [
    FilterOperator.NOT_EXISTS,
    FilterOperator.EXISTS,
    FilterOperator.OCCURS,
    FilterOperator.NOT_OCCURS,
  ].includes(operator);

/**
 * TODO: we wanna ditch this if possible as the new filter state is now FilterGroupState.
 *
 * - if attribute appears multiple times, we take the 1st appearance's values and ignore the rest
 *   (except for lte/gte combination which becomes "between" operator)
 */
export const getFilterStateFromFilter = (
  filter?: string | null
): FilterState => {
  if (!filter) return {};

  // Check if the filter is encoded in the FilterState format:
  // - this allows us to support the old way of storing filters in the URL
  try {
    if (isFilterStateCorrect(filter)) {
      return filter as any as FilterState;
    }
  } finally {
  }

  const attrs = splitIgnoringDoubleQuotes(filter, ",");

  return attrs.reduce((acc, filterPart) => {
    const fieldAndValue = filterPart.split(/=(.*)/s);

    const fieldNamePart = fieldAndValue[0];
    const valuePart = fieldAndValue[1];

    // Defensive: skip malformed parts (no value or missing operator colon)
    if (!valuePart || !valuePart.includes(":")) {
      return acc;
    }

    const apiOperator = valuePart.substring(
      0,
      valuePart.indexOf(":")
    ) as APIFilterOp;
    const operator = getOperatorFromAPIOperator(apiOperator);

    const values = valuePart.split(/:(.*)/s)[1];
    if (values === undefined) return acc;

    // If fieldNamePart already exists in the current filter string, we ignore this part of the filter
    if (getAttributesFromFilter(acc).includes(fieldNamePart)) return { ...acc };

    let valuesArray = getValuesArray(values);

    // when filter is embedded, we only have one value that we do not have to separate
    if (filterValuesHaveEmbeddedFilter(operator)) {
      valuesArray = [values];
    }

    return {
      ...acc,
      [fieldNamePart]: {
        values: valuesArray,
        operator,
      },
    };
  }, {} as FilterState);
};

export const formatAPIDate = (value: string, dataType?: DataType) => {
  if (value === "null") return value;

  switch (dataType) {
    case "date":
      return formatDate(value, API_DATE_FORMAT, true);
    case "dateWithTimeNoTZ":
      return formatDate(value, API_DATE_FORMAT_W_TIME);
    case "dateWithTimeUTC":
      return formatDate(value, ISO_FORMAT);
    default:
      return value;
  }
};

export const getSortFilter = (sortBy?: SortBy, schema?: SchemaEntry[]) => {
  if (!sortBy) return;

  return Object.entries(sortBy)
    .map(([key, value]) => {
      const keyOverride = schema?.find(({ accessor }) => accessor === key)?.sort
        ?.fieldNameForAPI;
      const keyForAPI = keyOverride || key;

      return `${keyForAPI}:${value}`;
    })
    .join(",");
};

export const hasPathTraversal = <Args>(args: Args): boolean => {
  if (args && typeof args === "object") {
    return Object.values(args)
      .filter((val) => val)
      .some(
        (value) =>
          value.toString().includes("/.") || value.toString().includes("../")
      );
  }

  return false;
};

export const createURL = (arr: string[]): string =>
  arr.map((s) => encodeURIComponent(s)).join("/");

export const VEHICLE_ATTRIBUTES_PREFIX = "vehicle.";
const NON_QUOTED_OPERATORS = ["occurs", "noccurs", "exists", "nexists"];

export const getFilterQuery = ({
  attribute,
  operator,
  values,
}: FilterRowState): string => {
  // Static filters route raw API values through here, so false and 0 are real values, not missing ones.
  if (
    !attribute ||
    !operator ||
    values === null ||
    values === undefined ||
    values.length === 0
  ) {
    return "";
  }

  let val =
    typeof values == "boolean" || NON_QUOTED_OPERATORS.includes(operator)
      ? values
      : `"${values}"`;

  if (Array.isArray(values) && !NON_QUOTED_OPERATORS.includes(operator)) {
    val = wrapInQuotesAndJoin(values);
  }

  const apiOperator = FILTER_OPERATOR_TO_API_FILTER[operator];

  return `${attribute}=${apiOperator}:${val}`;
};

// A resource that is not there is often a state a page has its own copy for, rather than an error to show raw.
export const isNotFoundError = (failure?: APIErrorResponse): boolean =>
  failure?.response?.status === 404;

export const isConflictError = (failure: unknown): boolean =>
  axios.isAxiosError(failure) && failure.response?.status === 409;

/**
 * Extracts a user-friendly error message from an axios error response.
 * Prioritizes the 'details' field from the API response (if it's a string),
 * falling back to the message field, then to the generic error message.
 *
 * @param error - The axios error object
 * @param fallbackMessage - Translated message used when the response carries no error details
 * @returns A user-friendly error message string
 */
export const extractErrorMessage = (
  error: any,
  fallbackMessage: string
): string => {
  const details = (error as any)?.response?.data?.details;
  const message = (error as any)?.response?.data?.message;
  const errorMessage = (error as any)?.message;

  // Only use details if it's a string, otherwise fall back to message
  if (typeof details === "string" && details.trim()) {
    return details;
  }

  return message || errorMessage || fallbackMessage;
};
